import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  postOnboardingReassign,
  postOnboardingSkip,
} from '../../api/accountsApi';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useToast } from '../../../hooks/useToast';
import { onboardingKeys, useOnboardingReassignCandidates } from '../../hooks/useOnboarding';
import {
  type BrandingRemoteFile,
  useFileEntries,
} from '../BrandIdentity/brandingShared';
import { OnboardingWizardFooter } from './OnboardingWizardFooter';
import { OnboardingOverlayContainerProvider } from './OnboardingOverlayContainerContext';
import {
  OnboardingAiPreferenceDialog,
  OnboardingStepWelcome,
  OnboardingWelcomePageHeader,
} from './steps/OnboardingStepWelcome';
import { useOnboardingLens } from './onboardingLens';
import { OnboardingWelcomeIllustration } from './onboardingIllustrations';
import { OnboardingStepTrainingPackage } from './steps/OnboardingStepTrainingPackage';
import { OnboardingStepCompany } from './steps/OnboardingStepCompany';
import { OnboardingStepTrainingPriorities } from './steps/OnboardingStepTrainingPriorities';
import { OnboardingStepScheduleAudience } from './steps/OnboardingStepScheduleAudience';
import { OnboardingStepBrand } from './steps/OnboardingStepBrand';
import { OnboardingStepComplete } from './steps/OnboardingStepComplete';
import { OnboardingStepLoading } from './steps/OnboardingStepLoading';
import { OnboardingStepReassign } from './steps/OnboardingStepReassign';
import { OnboardingReassignIllustration } from './onboardingIllustrations';
import { submitOnboardingWizard } from './onboardingSubmit';
import { ATLAS_LENS_COLORS } from '../Form/formFieldSpec';
import {
  emptyOnboardingFormData,
  type OnboardingFormData,
  type OnboardingStep6Phase,
  type OnboardingWizardStep,
  type OnboardingWizardView,
} from './OnboardingWizard.types';
import {
  isStep2Valid,
  isStep3Valid,
  isStep4Valid,
  isStep5Valid,
  isStep6Valid,
  validateStep2Fields,
  validateStep3Fields,
  validateStep4Fields,
  validateStep5Fields,
  validateStep6Fields,
} from './onboardingValidation';
import { trimTrainingTopicsToPackageLimit } from './onboardingConstants';
import {
  clearOnboardingAiTrainingFields,
  getOnboardingNextStep,
  getOnboardingPreviousStep,
  getOnboardingStepperProgress,
  resolveOnboardingAiFlowMode,
} from './onboardingGate';
import { onboardingBelowHeaderInsets } from './onboardingFlowBackground';

interface OnboardingWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  onStepAdvance?: (step: OnboardingWizardStep, formData: OnboardingFormData) => void;
  initialFormData?: Partial<OnboardingFormData>;
  orgId?: string;
  initialRemoteLogos?: BrandingRemoteFile[];
  initialRemoteDocuments?: BrandingRemoteFile[];
  /** Local preview: show Step 6 success after loading when orgId is missing (no API). */
  allowPreviewSubmit?: boolean;
  /** Page already renders onboarding background — keep dialog backdrop transparent. */
  transparentBackdrop?: boolean;
  /** Notifies test host when wizard submit succeeds or fails (step 7). */
  onSubmitResult?: (result: { success: boolean; errorMessage?: string }) => void;
}

export function OnboardingWizard({
  open,
  onClose: _onClose,
  onComplete,
  onStepAdvance,
  initialFormData,
  orgId,
  initialRemoteLogos = [],
  initialRemoteDocuments = [],
  allowPreviewSubmit = false,
  transparentBackdrop = false,
  onSubmitResult,
}: OnboardingWizardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { showError, showInfo } = useToast();
  const { profileData } = useUserProfile();
  const resolvedOrgId = orgId ?? profileData?.orgId;
  const { data: reassignCandidates, isLoading: isLoadingReassignCandidates } =
    useOnboardingReassignCandidates(resolvedOrgId, open && Boolean(resolvedOrgId));
  // Candidates exclude the current user — disable only when no colleague exists (solo org).
  const assignToColleagueDisabled =
    !isLoadingReassignCandidates && (reassignCandidates?.length ?? 0) === 0;
  const { layout, typography, tokens, surface, accentRedRequired: errorMessageColor } = useOnboardingLens();
  const { dialog, content, footer: footerLayout } = layout;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState<OnboardingWizardStep>(1);
  const [view, setView] = useState<OnboardingWizardView>('steps');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [showReassignErrors, setShowReassignErrors] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);
  const [isReassignSubmitting, setIsReassignSubmitting] = useState(false);
  const [isSkipSubmitting, setIsSkipSubmitting] = useState(false);
  const [formData, setFormData] = useState<OnboardingFormData>(() => ({
    ...emptyOnboardingFormData(),
    ...initialFormData,
  }));
  const [showStep2Errors, setShowStep2Errors] = useState(false);
  const [showStep3Errors, setShowStep3Errors] = useState(false);
  const [showStep4Errors, setShowStep4Errors] = useState(false);
  const [showStep5Errors, setShowStep5Errors] = useState(false);
  const [showStep6Errors, setShowStep6Errors] = useState(false);
  const [remoteLogos, setRemoteLogos] = useState<BrandingRemoteFile[]>(initialRemoteLogos);
  const [remoteDocuments, setRemoteDocuments] = useState<BrandingRemoteFile[]>(initialRemoteDocuments);
  const [pendingInactiveFileIds, setPendingInactiveFileIds] = useState<Set<string>>(() => new Set());
  const [step6Phase, setStep6Phase] = useState<OnboardingStep6Phase | null>(null);
  const [step6SubmittingAction, setStep6SubmittingAction] = useState<'finish' | 'skip' | null>(
    null,
  );
  const [overlayContainer, setOverlayContainer] = useState<HTMLElement | null>(null);
  const [aiAuthDialogOpen, setAiAuthDialogOpen] = useState(false);
  const [aiOptOutDialogOpen, setAiOptOutDialogOpen] = useState(false);

  const flowMode = resolveOnboardingAiFlowMode(formData.aiRecommendationEnabled);
  const stepperProgress = getOnboardingStepperProgress(activeStep, flowMode);

  const logoFiles = useFileEntries({
    resetWhenKey: open ? 'onboarding-open' : 'onboarding-closed',
    loadImages: true,
  });
  const documentFiles = useFileEntries({
    resetWhenKey: open ? 'onboarding-open' : 'onboarding-closed',
  });

  useEffect(() => {
    if (!open) {
      setActiveStep(1);
      setView('steps');
      setAssigneeUserId('');
      setShowReassignErrors(false);
      setReassignError(null);
      setIsReassignSubmitting(false);
      setIsSkipSubmitting(false);
      setShowStep2Errors(false);
      setShowStep3Errors(false);
      setShowStep4Errors(false);
      setShowStep5Errors(false);
      setShowStep6Errors(false);
      setRemoteLogos(initialRemoteLogos);
      setRemoteDocuments(initialRemoteDocuments);
      setPendingInactiveFileIds(new Set());
      setStep6Phase(null);
      setStep6SubmittingAction(null);
      setAiAuthDialogOpen(false);
      setAiOptOutDialogOpen(false);
      setFormData({
        ...emptyOnboardingFormData(),
        ...initialFormData,
      });
    }
  }, [open, initialFormData, initialRemoteLogos, initialRemoteDocuments]);

  const runSubmit = useCallback(
    async (data: OnboardingFormData) => {
      setActiveStep(6);
      setStep6Phase('submitting');

      const completeStep = (phase: OnboardingStep6Phase) => {
        const nextStep = 7 as OnboardingWizardStep;
        setStep6SubmittingAction(null);
        setActiveStep(nextStep);
        setStep6Phase(phase);
        onStepAdvance?.(nextStep, data);
      };

      if (!resolvedOrgId) {
        if (allowPreviewSubmit) {
          completeStep('success');
          onSubmitResult?.({ success: true });
          return;
        }
        completeStep('error');
        onSubmitResult?.({
          success: false,
          errorMessage: 'Organization ID not found. Please try again.',
        });
        return;
      }

      if (data.aiRecommendationEnabled === true && !isStep2Valid(data.trainingPackage)) {
        completeStep('error');
        onSubmitResult?.({
          success: false,
          errorMessage: t('onboarding.wizard.step2.errors.required'),
        });
        return;
      }

      try {
        await submitOnboardingWizard({
          orgId: resolvedOrgId,
          formData: data,
          logoEntries: logoFiles.entries,
          documentEntries: documentFiles.entries,
          pendingInactiveFileIds,
        });
        completeStep('success');
        onSubmitResult?.({ success: true });
      } catch (error) {
        console.error('Onboarding submit failed', error);
        completeStep('error');
        onSubmitResult?.({
          success: false,
          errorMessage:
            error instanceof Error
              ? error.message
              : 'Something went wrong saving your setup. Please try again.',
        });
      }
    },
    [resolvedOrgId, allowPreviewSubmit, logoFiles.entries, documentFiles.entries, pendingInactiveFileIds, onSubmitResult, onStepAdvance, t],
  );

  const step3FieldErrors = useMemo(
    () =>
      validateStep3Fields(
        formData.companyName,
        formData.industry,
        formData.employeeCount,
        formData.regions,
        formData.employeeTerminology,
        formData.speakUpSolution,
      ),
    [
      formData.companyName,
      formData.industry,
      formData.employeeCount,
      formData.regions,
      formData.employeeTerminology,
      formData.speakUpSolution,
    ],
  );

  const step4FieldErrors = useMemo(
    () => validateStep4Fields(formData.trainingTopics, formData.trainingPackage),
    [formData.trainingTopics, formData.trainingPackage],
  );

  const step5FieldErrors = useMemo(
    () =>
      validateStep5Fields(
        formData.priorityMonths,
        formData.monthsToAvoid,
        formData.additionalContext,
      ),
    [formData.priorityMonths, formData.monthsToAvoid, formData.additionalContext],
  );

  const visibleRemoteLogoCount = useMemo(
    () => remoteLogos.filter((file) => !pendingInactiveFileIds.has(file.id)).length,
    [remoteLogos, pendingInactiveFileIds],
  );
  const visibleRemoteDocumentCount = useMemo(
    () => remoteDocuments.filter((file) => !pendingInactiveFileIds.has(file.id)).length,
    [remoteDocuments, pendingInactiveFileIds],
  );

  const step6FieldErrors = useMemo(
    () =>
      validateStep6Fields(
        formData.brandSkipped,
        formData.brandGuidelines,
        formData.primaryBrandColor,
        formData.secondaryBrandColor,
        logoFiles.entries.length + visibleRemoteLogoCount,
        documentFiles.entries.length + visibleRemoteDocumentCount,
      ),
    [
      formData.brandSkipped,
      formData.brandGuidelines,
      formData.primaryBrandColor,
      formData.secondaryBrandColor,
      logoFiles.entries.length,
      documentFiles.entries.length,
      visibleRemoteLogoCount,
      visibleRemoteDocumentCount,
    ],
  );

  const step2FieldErrors = useMemo(
    () => validateStep2Fields(formData.trainingPackage),
    [formData.trainingPackage],
  );

  const canContinueStep3 = isStep3Valid(
    formData.companyName,
    formData.industry,
    formData.employeeCount,
    formData.regions,
    formData.employeeTerminology,
    formData.speakUpSolution,
  );
  const canContinueStep4 = isStep4Valid(formData.trainingTopics, formData.trainingPackage);

  const canContinueStep5 = isStep5Valid(
    formData.priorityMonths,
    formData.monthsToAvoid,
    formData.additionalContext,
  );

  const canContinueStep6 = isStep6Valid(
    formData.brandSkipped,
    formData.brandGuidelines,
    formData.primaryBrandColor,
    formData.secondaryBrandColor,
    logoFiles.entries.length + visibleRemoteLogoCount,
    documentFiles.entries.length + visibleRemoteDocumentCount,
  );

  const canContinueStep2 = isStep2Valid(formData.trainingPackage);

  const canContinue =
    activeStep === 1
      ? true
      : activeStep === 2
        ? canContinueStep2
        : activeStep === 3
          ? canContinueStep3
          : activeStep === 4
            ? canContinueStep4
            : activeStep === 5
              ? canContinueStep5
              : activeStep === 6
                ? canContinueStep6
                : false;

  const showIllustration =
    (activeStep === 1 && view === 'steps' && !isMobile) ||
    (view === 'reassign' && !isMobile);

  const invalidateOnboardingQueries = useCallback(async () => {
    if (!resolvedOrgId) {
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: onboardingKeys.settings(resolvedOrgId) }),
      queryClient.invalidateQueries({ queryKey: onboardingKeys.bannerStatus(resolvedOrgId) }),
    ]);
  }, [resolvedOrgId, queryClient]);

  const finishSkipFlow = useCallback(async () => {
    await invalidateOnboardingQueries();
    onComplete?.();
  }, [invalidateOnboardingQueries, onComplete]);

  const missingOrganizationError = t('onboarding.wizard.errors.missing_organization');

  const handleSkipDoLater = useCallback(async () => {
    if (!resolvedOrgId) {
      showError(missingOrganizationError);
      return;
    }
    setIsSkipSubmitting(true);
    try {
      await postOnboardingSkip(resolvedOrgId, 'deferred_later');
      await finishSkipFlow();
    } catch (error) {
      console.error('Onboarding defer failed', error);
      showError(
        error instanceof Error
          ? error.message
          : t('onboarding.wizard.step1.skip_error'),
      );
    } finally {
      setIsSkipSubmitting(false);
    }
  }, [finishSkipFlow, missingOrganizationError, resolvedOrgId, showError, t]);

  const handleSkipNotMyTask = useCallback(() => {
    if (!resolvedOrgId) {
      showError(missingOrganizationError);
      return;
    }
    setView('reassign');
    setAssigneeUserId('');
    setShowReassignErrors(false);
    setReassignError(null);
  }, [missingOrganizationError, resolvedOrgId, showError]);

  const handleReassignDone = useCallback(async () => {
    if (!assigneeUserId) {
      setShowReassignErrors(true);
      return;
    }
    if (!resolvedOrgId) {
      showError(missingOrganizationError);
      return;
    }

    setShowReassignErrors(false);
    setReassignError(null);
    setIsReassignSubmitting(true);
    try {
      const result = await postOnboardingReassign(resolvedOrgId, assigneeUserId);
      if (!result) {
        throw new Error(t('onboarding.wizard.reassign.submit_error'));
      }
      if (!result.emailQueued) {
        showInfo(
          result.emailError
            ? t('onboarding.wizard.reassign.email_not_queued_detail', {
                detail: result.emailError,
              })
            : t('onboarding.wizard.reassign.email_not_queued'),
        );
      } else if (result.recipientEmail) {
        showInfo(
          t('onboarding.wizard.reassign.email_queued', {
            email: result.recipientEmail,
          }),
        );
      }
      await finishSkipFlow();
    } catch (error) {
      console.error('Onboarding reassign failed', error);
      const message =
        error instanceof Error
          ? error.message
          : t('onboarding.wizard.reassign.submit_error');
      showError(message);
      setReassignError(message);
    } finally {
      setIsReassignSubmitting(false);
    }
  }, [assigneeUserId, finishSkipFlow, missingOrganizationError, resolvedOrgId, showError, showInfo, t]);

  const responsiveDialogSize = isMobile
    ? { width: '100%', height: '100%' }
    : transparentBackdrop
      ? {
          width: `min(${dialog.width}, calc(100vw - ${dialog.margin}))`,
          maxWidth: `min(${dialog.width}, calc(100vw - ${dialog.margin}))`,
          height: `min(${dialog.height}, calc(100vh - ${dialog.margin} - var(--atlas-global-header-height, 64px)))`,
          minHeight: `min(${dialog.height}, calc(100vh - ${dialog.margin} - var(--atlas-global-header-height, 64px)))`,
          maxHeight: `min(${dialog.height}, calc(100vh - ${dialog.margin} - var(--atlas-global-header-height, 64px)))`,
        }
      : {
          width: `min(${dialog.width}, calc(100vw - ${dialog.margin}))`,
          maxWidth: `min(${dialog.width}, calc(100vw - ${dialog.margin}))`,
          height: `min(${dialog.height}, calc(100vh - ${dialog.margin}))`,
          minHeight: `min(${dialog.height}, calc(100vh - ${dialog.margin}))`,
          maxHeight: `min(${dialog.height}, calc(100vh - ${dialog.margin}))`,
        };

  const handleContinue = useCallback(() => {
    if (activeStep === 2) {
      if (!canContinueStep2) {
        setShowStep2Errors(true);
        return;
      }
    } else if (activeStep === 3) {
      if (!canContinueStep3) {
        setShowStep3Errors(true);
        return;
      }
    } else if (activeStep === 4) {
      if (!canContinueStep4) {
        setShowStep4Errors(true);
        return;
      }
    } else if (activeStep === 5) {
      if (!canContinueStep5) {
        setShowStep5Errors(true);
        return;
      }
    } else if (activeStep === 6) {
      if (!canContinueStep6) {
        setShowStep6Errors(true);
        return;
      }
      const nextFormData = { ...formData, brandSkipped: false };
      setFormData(nextFormData);
      setShowStep6Errors(false);
      setStep6SubmittingAction('finish');
      void runSubmit(nextFormData);
      return;
    } else {
      return;
    }

    const nextStep = getOnboardingNextStep(activeStep, flowMode);
    if (!nextStep) {
      return;
    }

    if (activeStep === 5 && nextStep === 6) {
      const nextFormData = { ...formData, brandSkipped: false };
      setFormData(nextFormData);
      setActiveStep(nextStep);
      onStepAdvance?.(nextStep, nextFormData);
      return;
    }

    setActiveStep(nextStep);
    onStepAdvance?.(nextStep, formData);
  }, [
    activeStep,
    canContinueStep2,
    canContinueStep3,
    canContinueStep4,
    canContinueStep5,
    canContinueStep6,
    flowMode,
    formData,
    onStepAdvance,
    runSubmit,
  ]);

  const handleSetupWithAi = useCallback(() => {
    setAiAuthDialogOpen(true);
  }, []);

  const handleSetupWithAiConfirmed = useCallback(() => {
    setAiAuthDialogOpen(false);
    setFormData((prev) => ({ ...prev, aiRecommendationEnabled: true }));
    const nextStep = 2 as OnboardingWizardStep;
    setActiveStep(nextStep);
    onStepAdvance?.(nextStep, { ...formData, aiRecommendationEnabled: true });
  }, [formData, onStepAdvance]);

  const handleSetupWithoutAi = useCallback(() => {
    setAiOptOutDialogOpen(true);
  }, []);

  const handleSetupWithoutAiConfirmed = useCallback(() => {
    setAiOptOutDialogOpen(false);
    const nextFormData = clearOnboardingAiTrainingFields({
      ...formData,
      aiRecommendationEnabled: false,
    });
    setFormData(nextFormData);
    const nextStep = 3 as OnboardingWizardStep;
    setActiveStep(nextStep);
    onStepAdvance?.(nextStep, nextFormData);
  }, [formData, onStepAdvance]);

  const handleDoThisLater = useCallback(() => {
    const nextFormData = { ...formData, brandSkipped: true };
    setFormData(nextFormData);
    setShowStep6Errors(false);
    setStep6SubmittingAction('skip');
    void runSubmit(nextFormData);
  }, [formData, runSubmit]);

  const handleRetrySubmit = useCallback(() => {
    setStep6SubmittingAction('finish');
    void runSubmit(formData);
  }, [formData, runSubmit]);

  const handleGoToCourses = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  const handleBack = () => {
    if (view === 'reassign') {
      setView('steps');
      setAssigneeUserId('');
      setShowReassignErrors(false);
      setReassignError(null);
      return;
    }
    if (activeStep === 7) {
      if (step6Phase === 'success') return;
      setStep6Phase(null);
      setActiveStep(6);
      return;
    }

    const previousStep = getOnboardingPreviousStep(activeStep, flowMode);
    if (!previousStep) {
      return;
    }

    if (activeStep === 2) setShowStep2Errors(false);
    if (activeStep === 3) setShowStep3Errors(false);
    if (activeStep === 4) setShowStep4Errors(false);
    if (activeStep === 5) setShowStep5Errors(false);
    if (activeStep === 6) setShowStep6Errors(false);
    setActiveStep(previousStep);
  };

  const handleMarkRemoteFileInactive = (fileId: string) => {
    setPendingInactiveFileIds((prev) => new Set(prev).add(fileId));
  };

  const renderStepContent = () => {
    if (view === 'reassign') {
      return (
        <OnboardingStepReassign
          orgId={resolvedOrgId}
          currentUserId={profileData?.userId}
          selectedUserId={assigneeUserId}
          onSelectedUserIdChange={setAssigneeUserId}
          showErrors={showReassignErrors}
          submitError={reassignError}
        />
      );
    }

    switch (activeStep) {
      case 1:
        return <OnboardingStepWelcome />;
      case 2:
        return (
          <OnboardingStepTrainingPackage
            trainingPackage={formData.trainingPackage}
            onTrainingPackageChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                trainingPackage: value,
                trainingTopics: trimTrainingTopicsToPackageLimit(prev.trainingTopics, value),
              }))
            }
            showErrors={showStep2Errors}
            fieldErrors={step2FieldErrors}
          />
        );
      case 3:
        return (
          <OnboardingStepCompany
            companyName={formData.companyName}
            industry={formData.industry}
            employeeCount={formData.employeeCount}
            regions={formData.regions}
            employeeTerminology={formData.employeeTerminology}
            speakUpSolution={formData.speakUpSolution}
            onCompanyNameChange={(value) =>
              setFormData((prev) => ({ ...prev, companyName: value }))
            }
            onIndustryChange={(value) => setFormData((prev) => ({ ...prev, industry: value }))}
            onEmployeeCountChange={(value) =>
              setFormData((prev) => ({ ...prev, employeeCount: value }))
            }
            onRegionsChange={(value) => setFormData((prev) => ({ ...prev, regions: value }))}
            onEmployeeTerminologyChange={(value) =>
              setFormData((prev) => ({ ...prev, employeeTerminology: value }))
            }
            onSpeakUpSolutionChange={(value) =>
              setFormData((prev) => ({ ...prev, speakUpSolution: value }))
            }
            showErrors={showStep3Errors}
            fieldErrors={step3FieldErrors}
          />
        );
      case 4:
        return (
          <OnboardingStepTrainingPriorities
            trainingPackage={formData.trainingPackage}
            trainingTopics={formData.trainingTopics}
            onTrainingTopicsChange={(value) =>
              setFormData((prev) => ({ ...prev, trainingTopics: value }))
            }
            showErrors={showStep4Errors}
            fieldErrors={step4FieldErrors}
          />
        );
      case 5:
        return (
          <OnboardingStepScheduleAudience
            priorityMonths={formData.priorityMonths}
            monthsToAvoid={formData.monthsToAvoid}
            additionalContext={formData.additionalContext}
            onPriorityMonthsChange={(value) =>
              setFormData((prev) => ({ ...prev, priorityMonths: value }))
            }
            onMonthsToAvoidChange={(value) =>
              setFormData((prev) => ({ ...prev, monthsToAvoid: value }))
            }
            onAdditionalContextChange={(value) =>
              setFormData((prev) => ({ ...prev, additionalContext: value }))
            }
            showErrors={showStep5Errors}
            fieldErrors={step5FieldErrors}
          />
        );
      case 6:
        if (step6Phase === 'submitting' && flowMode === 'with_ai') {
          return <OnboardingStepLoading />;
        }
        return (
          <OnboardingStepBrand
            orgId={orgId}
            brandGuidelines={formData.brandGuidelines}
            primaryBrandColor={formData.primaryBrandColor}
            secondaryBrandColor={formData.secondaryBrandColor}
            logoEntries={logoFiles.entries}
            documentEntries={documentFiles.entries}
            remoteLogos={remoteLogos}
            remoteDocuments={remoteDocuments}
            pendingInactiveFileIds={pendingInactiveFileIds}
            onBrandGuidelinesChange={(value) => {
              setShowStep6Errors(false);
              setFormData((prev) => ({ ...prev, brandGuidelines: value }));
            }}
            onPrimaryBrandColorChange={(value) =>
              setFormData((prev) => ({ ...prev, primaryBrandColor: value }))
            }
            onSecondaryBrandColorChange={(value) =>
              setFormData((prev) => ({ ...prev, secondaryBrandColor: value }))
            }
            onLogoAdd={(event) => {
              logoFiles.addFiles(event);
            }}
            onDocumentAdd={(event) => {
              documentFiles.addFiles(event);
            }}
            onRemoveLocalLogo={logoFiles.removeEntry}
            onRemoveLocalDocument={documentFiles.removeEntry}
            onRemoveRemoteFile={handleMarkRemoteFileInactive}
            showErrors={showStep6Errors}
            fieldErrors={step6FieldErrors}
          />
        );
      case 7:
        if (step6Phase === 'success') {
          return <OnboardingStepComplete hasTrainingPlan={flowMode === 'with_ai'} />;
        }
        if (step6Phase === 'error') {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: layout.formSection.sectionGap,
                height: '100%',
                minHeight: layout.content.viewportMinHeight,
                px: layout.formSection.paddingX,
                textAlign: 'center',
                backgroundColor: 'var(--lens-semantic-color-surface-default)',
              }}
            >
              <Typography
                role="alert"
                sx={{
                  ...typography.body,
                  color: errorMessageColor,
                }}
              >
                {t('onboarding.wizard.step7.submit_error')}
              </Typography>
            </Box>
          );
        }
        return null;
      default:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', px: 4 }}>
            <Typography color="text.secondary">
              {t('onboarding.wizard.coming_soon', { step: activeStep })}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      disableEscapeKeyDown
      disableEnforceFocus={transparentBackdrop}
      disableScrollLock={transparentBackdrop}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
      }}
      sx={
        transparentBackdrop && !isMobile
          ? {
              position: 'fixed',
              ...onboardingBelowHeaderInsets,
              width: '100%',
              zIndex: (theme) => theme.zIndex.modal,
              pointerEvents: 'none',
              '& .MuiBackdrop-root': {
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
              },
              '& .MuiDialog-container': {
                position: 'absolute',
                inset: 0,
                height: '100%',
                pointerEvents: 'none',
              },
              '& .MuiDialog-paper': {
                pointerEvents: 'auto',
              },
            }
          : undefined
      }
      PaperProps={{
        ref: setOverlayContainer,
        sx: {
          position: 'relative',
          ...responsiveDialogSize,
          borderRadius: isMobile ? 0 : dialog.borderRadius,
          overflow: 'hidden',
          boxShadow: dialog.shadow,
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: isMobile
            ? '1fr auto'
            : `${content.height} ${footerLayout.height}`,
          alignContent: 'start',
          pb: isMobile ? 0 : dialog.paddingBottom,
          m: isMobile ? 0 : dialog.margin,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: transparentBackdrop
              ? 'transparent'
              : ATLAS_LENS_COLORS.backgroundBackdrop,
          },
        },
      }}
    >
      <OnboardingOverlayContainerProvider container={overlayContainer}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          width: '100%',
          height: isMobile ? 'auto' : '100%',
          minHeight: isMobile ? content.height : 0,
          overflow: 'hidden',
        }}
      >
        {showIllustration ? (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              backgroundColor:
                tokens.semantic?.color?.background?.base?.value ?? surface,
            }}
          >
            {activeStep === 1 && view === 'steps' ? <OnboardingWelcomePageHeader /> : null}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flex: 1,
                minHeight: 0,
                width: '100%',
                height:
                  activeStep === 1 && view === 'steps'
                    ? layout.welcomeStep1.contentRow.height
                    : 'auto',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: '0 0 auto',
                  width:
                    activeStep === 1 && view === 'steps'
                      ? layout.welcomeStep1.leftColumn.width
                      : layout.welcome.leftColumnWidth,
                  maxWidth:
                    activeStep === 1 && view === 'steps'
                      ? layout.welcomeStep1.leftColumn.width
                      : layout.welcome.leftColumnWidth,
                  minWidth: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 1,
                  overflow: 'hidden',
                }}
              >
                {renderStepContent()}
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  left:
                    activeStep === 1 && view === 'steps'
                      ? layout.welcomeStep1.illustration.left
                      : layout.illustration.left,
                  top: '50%',
                  transform: 'translateY(calc(-50% + var(--lens-core-spacing-px)))',
                  width:
                    activeStep === 1 && view === 'steps'
                      ? layout.welcomeStep1.illustration.width
                      : layout.illustration.width,
                  height:
                    activeStep === 1 && view === 'steps'
                      ? layout.welcomeStep1.illustration.height
                      : layout.illustration.height,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                {view === 'reassign' ? (
                  <OnboardingReassignIllustration />
                ) : (
                  <OnboardingWelcomeIllustration />
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: isMobile ? 'auto' : '100%',
              minHeight: isMobile ? content.height : 0,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor:
                tokens.semantic?.color?.background?.base?.value ?? surface,
              overflow: 'hidden',
            }}
          >
            {activeStep === 1 && view === 'steps' ? <OnboardingWelcomePageHeader /> : null}
            {renderStepContent()}
          </Box>
        )}
      </Box>

      <OnboardingWizardFooter
        activeStep={activeStep}
        view={view}
        step6Phase={step6Phase}
        step6SubmittingAction={step6SubmittingAction}
        isMobile={isMobile}
        canContinue={canContinue}
        stepperCurrent={stepperProgress.current}
        stepperTotal={stepperProgress.total}
        reassignCanSubmit={Boolean(assigneeUserId)}
        isReassignSubmitting={isReassignSubmitting}
        isSkipSubmitting={isSkipSubmitting}
        onBack={handleBack}
        onContinue={handleContinue}
        onDoThisLater={handleDoThisLater}
        onGoToCourses={handleGoToCourses}
        onRetrySubmit={handleRetrySubmit}
        onReassignDone={() => void handleReassignDone()}
        onSkipDoLater={() => void handleSkipDoLater()}
        onSkipNotMyTask={handleSkipNotMyTask}
        assignToColleagueDisabled={assignToColleagueDisabled}
        onSetupWithAi={handleSetupWithAi}
        onSetupWithoutAi={handleSetupWithoutAi}
        hasTrainingPlan={flowMode === 'with_ai'}
      />

      <OnboardingAiPreferenceDialog
        variant="authorize"
        open={aiAuthDialogOpen}
        onClose={() => setAiAuthDialogOpen(false)}
        onConfirm={handleSetupWithAiConfirmed}
      />
      <OnboardingAiPreferenceDialog
        variant="opt_out"
        open={aiOptOutDialogOpen}
        onClose={() => setAiOptOutDialogOpen(false)}
        onConfirm={handleSetupWithoutAiConfirmed}
      />
      </OnboardingOverlayContainerProvider>
    </Dialog>
  );
}
